/**
 * The Lookout item in the RudderStack web app's sidebar links to `/login?from=rudderstack` (ACT2-808).
 * Its visitors are RudderStack users by definition, so a signed-out one skips the sign-in card and
 * goes straight into the RudderStack sign-in. With Lookout's own sign-in (ACT2-871), the login page
 * serves the visit itself.
 */
const WEB_APP_FLAG = { key: 'from', value: 'rudderstack' } as const;

/** The `entry` value that marks a sign-in from the sidebar. The backend counts only this exact value. */
export const WEB_APP_ENTRY = 'rudderstack_app';

/** Campaign keys the link carries. The Lookout backend reads them out of return_to for signup attribution. */
const CAMPAIGN_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

/**
 * Mirrors the caps in the backend's `src/lib/campaign-attribution.ts`, which is the authority: it
 * re-reads and re-cleans whatever arrives in `return_to`. They are repeated rather than imported
 * because the `@server/*` aliases carry types into this bundle, not runtime code.
 *
 * `/login` is user-supplied, so its campaign is too. Without the caps a crafted link would build a
 * redirect whose query outgrows Node's 16 KiB header budget, and the visitor would get a blank 431
 * from the sign-in they clicked. Per value, in code points; then for the serialised query.
 */
const MAX_VALUE_LENGTH = 200;
const MAX_QUERY_LENGTH = 1536;

/** Clipped by code point, so a surrogate pair is never cut in half. */
function clip(value: string): string {
  return Array.from(value).slice(0, MAX_VALUE_LENGTH).join('');
}

/**
 * The return path for a visit from the web app's sidebar: `/` with the link's campaign keys. Null when
 * the visit has no flag, or has an `error` (a failed sign-in comes back to /login with one, and
 * starting it again would loop).
 */
export function webAppEntryReturnTo(search: string): string | null {
  const params = new URLSearchParams(search);
  if (params.get(WEB_APP_FLAG.key) !== WEB_APP_FLAG.value || params.has('error')) return null;

  // Key order is fixed, and a key that would breach the total cap is skipped rather than truncated,
  // so the keys that fit stay intact — the same rule as the backend's pickCampaignQuery.
  const campaign = new URLSearchParams();
  for (const key of CAMPAIGN_KEYS) {
    const value = params.get(key);
    if (!value) continue;
    const candidate = new URLSearchParams(campaign);
    candidate.set(key, clip(value));
    if (candidate.toString().length > MAX_QUERY_LENGTH) continue;
    campaign.set(key, clip(value));
  }
  // Not `campaign.size`: it shipped in Chrome 113 and Safari 17, while Vite's default target is
  // chrome111/edge111/firefox114/safari16.4/ios16.4 — so Chrome and Edge 111–112 and Safari and
  // iOS 16.4–16.6 are in-target and lack it, and a missing accessor is not something a build
  // target polyfills. There `undefined > 0` is false, which would silently drop the campaign.
  // (Firefox is not affected: the floor is 114 and `size` landed in 112.)
  const query = campaign.toString();
  return query ? `/?${query}` : '/';
}

/**
 * The Lookout backend URL that starts the RudderStack OAuth sign-in for a visit from the web app's
 * sidebar, or null when the visit should see the sign-in card (see `webAppEntryReturnTo`).
 */
export function webAppEntrySignInUrl(search: string): string | null {
  const returnTo = webAppEntryReturnTo(search);
  return returnTo === null
    ? null
    : `/auth/rudderstack?entry=${WEB_APP_ENTRY}&return_to=${encodeURIComponent(returnTo)}`;
}
